import { useQuery } from '@tanstack/react-query';

import { QUERY_KEYS } from 'api/constants';
import { resolve } from 'api/resolve';

import { useProjectTeamApi } from '../client-projectTeamApi';

interface UseProjectTeamByProjectIdOptions {
  enabled?: boolean;
  /** See `useProjectTeams` — opt out where a component mounts often enough to storm a failing backend. */
  retryOnMount?: boolean;
}

/**
 * The project team for one project. Prefer this over `useProjectTeams` on project pages: that one
 * returns every team in the subscription, including other projects' roles.
 *
 * Keyed under `PROJECT_TEAMS` so anything invalidating the team list invalidates this too.
 */
export function useProjectTeamByProjectId(
  projectId: string,
  { enabled = true, retryOnMount }: UseProjectTeamByProjectIdOptions = {}
) {
  const { getProjectTeamByProjectId } = useProjectTeamApi();

  const { data, isLoading, isError } = useQuery({
    queryKey: [QUERY_KEYS.PROJECT_TEAMS, 'project', projectId],
    queryFn: () => resolve(getProjectTeamByProjectId(projectId)),
    enabled: enabled && !!projectId,
    retryOnMount: retryOnMount ?? true
  });

  return { data, isLoading, isError };
}
